import Layout from "../../components/reusable/Layout";
import ChartBars from "../../components/reusable/ChartBars";
import CardDescription from "../../components/reusable/CardDescription";
import SuggestionCard from "../../components/SuggestionCard";
import MasonCollinsSuggestionCard from "../../components/MasonCollinsSuggestionCard";
import Qrcode from "../../components/reusable/Qrcode";

import classes from "./MetricPage.module.css";
import CustomSuggestionCard from "../../components/CustomSuggestionCard";
import SuggestionCardWithImages from "../../components/SuggestionCardWithImages";
import SyncLifeCustomPage from "../SyncLifeCustomPage/SyncLifeCustomPage";
import { GNC_SUPPLEMENTS_DATA } from "./gncSupplementsData";
import { ALLURA_THERAPY_DATA } from "./alluraTherapyData";
import { MASON_COLLINS_SUPPLEMENT_DATA } from "./masonCollinsSupplementData";

type Tab = { INDEX: number; NAME: string; VALUE: number };

type improveArea = { INDEX: number; TEXT: string; TITLE: string };

type Metric = {
  HOW_IT_IS_MEASURED: string;
  HOW_IT_IS_MEASURED_TEXT: string;
  INDEX: number;
  SUGGESTIONS: { IMPROVE_AREAS: improveArea[]; INDEX: number; TITLE: string }[];
  TITLE1: string;
  TITLE2: string;
  TOP: string;
  VALUE: number;
  WHAT_IT_IS: string;
  WHAT_IT_IS_TEXT: string;
  WHY_ITS_IMPORTANT: string;
  WHY_ITS_IMPORTANT_TEXT: string;
  RECCOMENDATIONS_TO_IMPROVE: string;
  SOURCES?: any;
  METRIC_TYPE: number;
};

interface Props {
  data: Metric[];
  isAMRReport?: boolean;
  navBarData: Tab[];
  isRetoReport?: boolean;
  qrCodeData: {
    FOOTER1: string;
    HEADER1: string;
    HEADER2: string;
    QRCODE: string;
  };
  isNaturalSlimReport?: boolean;
  isSyncLifeReport?: boolean;
  isEvityReport?: boolean;
  isGNCReport?: boolean;
  isAlluraReport?: boolean;
  isMasonCollinsReport?: boolean;
  //     {
  //   DESCRIPTION: string;
  //   INDEX: number;
  //   METRICS: Metric[];
  //
  //   SOURCES: any;
  //   RISK_METRICS: Metric[];
  //   PERFORMANCE_METRICS: Metric[];
  //   QRCODE: {
  //     FOOTER1: string;
  //     HEADER1: string;
  //     HEADER2: string;
  //     QRCODE: string;
  //   };
  //   TITLE: string;
  //   TITLE_PERFORMANCE_METRICS: string;
  // };
}

const MetricPage = ({
  data,
  isAMRReport,
  navBarData,
  qrCodeData,
  isRetoReport,
  isNaturalSlimReport,
  isSyncLifeReport,
  isEvityReport,
  isGNCReport,
  isAlluraReport,
  isMasonCollinsReport,
}: Props): JSX.Element => {
  return (
    <>
      {data.map((metric) => (
        <>
          <Layout
            key={`${metric.INDEX} ${metric.TITLE1}`}
            isEvityReport={isEvityReport}
          >
            <div className={classes.chart}>
              <ChartBars
                id={metric.METRIC_TYPE}
                title1={metric.TITLE1}
                value={metric.VALUE}
                title2={metric.TITLE2}
                labelsIsVisible={true}
                useLargerFont={true}
                customGap={true}
                labels={navBarData.slice(0, -1)}
                showIcon={true}
                iconLabel={metric.TOP}
              />
            </div>

            <ul className={classes.inner}>
              <li className={classes.sub}>
                <CardDescription
                  title={metric.HOW_IT_IS_MEASURED}
                  description={metric.HOW_IT_IS_MEASURED_TEXT}
                  useLargerFont={true}
                  index={metric.INDEX}
                />
              </li>
              <li className={classes.sub}>
                <CardDescription
                  title={metric.WHY_ITS_IMPORTANT}
                  description={metric.WHY_ITS_IMPORTANT_TEXT}
                  useLargerFont={true}
                  index={metric.INDEX}
                />
              </li>
            </ul>
            <div className={classes.card_container}>
              <div className={classes.suggestion_tables_title}>
                {metric.RECCOMENDATIONS_TO_IMPROVE}
              </div>
              <ul
                className={classes.suggestion_tables}
                style={{
                  backgroundColor: isNaturalSlimReport
                    ? "#5CB0A8"
                    : "transparent",
                }}
              >
                {isAlluraReport && ALLURA_THERAPY_DATA[metric.METRIC_TYPE]
                  ? ALLURA_THERAPY_DATA[metric.METRIC_TYPE].map(
                      (rowData, idx) => (
                        <SuggestionCard key={idx} data={rowData} />
                      )
                    )
                  : metric.SUGGESTIONS.map((item, index: number) => {
                      if (isNaturalSlimReport) {
                        return (
                          <SuggestionCardWithImages key={index} data={item} />
                        );
                      }
                      // For GNC: replace 3rd card (INDEX 3) with supplements, or hide if no data
                      if (isGNCReport && item.INDEX === 3) {
                        const supplementData =
                          GNC_SUPPLEMENTS_DATA[metric.METRIC_TYPE];
                        if (!supplementData) return null;
                        return (
                          <SuggestionCard key={index} data={supplementData} />
                        );
                      }
                      // For Mason Collins: replace the "Nutrition" row (INDEX 2)
                      // with Performance Support supplements
                      if (isMasonCollinsReport && item.INDEX === 2) {
                        const supplementData =
                          MASON_COLLINS_SUPPLEMENT_DATA[metric.METRIC_TYPE];
                        if (supplementData) {
                          return (
                            <MasonCollinsSuggestionCard
                              key={index}
                              data={supplementData}
                            />
                          );
                        }
                      }
                      return item.INDEX > 3 ? (
                        <CustomSuggestionCard key={index} data={item} />
                      ) : (
                        <SuggestionCard key={index} data={item} />
                      );
                    })}
              </ul>
            </div>
            {!isRetoReport && metric.SOURCES.length > 0 && (
              <Qrcode
                data={qrCodeData}
                sources={metric.SOURCES}
                isNaturalSlimReport={isNaturalSlimReport}
              />
            )}
          </Layout>
          {(() => {
            // Define metric type groups for SyncLife custom pages
            const SYNC_LIFE_AMR_METRICS = [10, 18, 17];
            const SYNC_LIFE_NON_AMR_METRICS = [25, 5, 4];
            // Check if metric type matches the report context
            const shouldShowCustomPage =
              (isSyncLifeReport &&
                isAMRReport &&
                SYNC_LIFE_AMR_METRICS.includes(metric.METRIC_TYPE)) ||
              (isSyncLifeReport &&
                !isAMRReport &&
                SYNC_LIFE_NON_AMR_METRICS.includes(metric.METRIC_TYPE));

            return (
              shouldShowCustomPage && (
                <SyncLifeCustomPage id={metric.METRIC_TYPE} />
              )
            );
          })()}
        </>
      ))}
    </>
  );
};

export default MetricPage;
